/**
 * Messages Page
 *
 * Main messaging interface with conversation list, message thread, and composer.
 * Used by both guides and customers.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-476 - Core Messaging Infrastructure (13 SP)
 * Location: apps/web/src/pages/Messages.jsx
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useMessages from '../../hooks/useMessages';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ConversationList from '../../components/messages/ConversationList';
import MessageThread from '../../components/messages/MessageThread';
import MessageComposer from '../../components/messages/MessageComposer';
import QuickReplySelector from '../../components/messages/QuickReplySelector';

const Messages = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    conversations,
    selectedConversationId,
    messages,
    unreadCount,
    loadingConversations,
    loadingMessages,
    sending,
    conversationsError,
    messagesError,
    sendMessage,
    selectConversation,
  } = useMessages();

  const [_error, _setError] = useState(null);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [templateInjection, setTemplateInjection] = useState({ text: '', key: null });

  // Get selected conversation details
  const selectedConversation = conversations.find((c) => c.id === selectedConversationId);

  // Get recipient ID for sending messages
  const recipientId = selectedConversation
    ? selectedConversation.participant_id
    : null;

  // Handle sending message
  const handleSendMessage = async (messageText) => {
    if (!selectedConversationId || !recipientId) {
      _setError('No conversation selected');
      setTimeout(() => _setError(null), 3000);
      return;
    }

    try {
      await sendMessage(selectedConversationId, recipientId, messageText);
    } catch (error) {
      _setError(error.message);
      setTimeout(() => _setError(null), 3000);
    }
  };

  // Handle template selection — QuickReplySelector already resolves variables,
  // so `content` is the final text. Key changes each time so the composer
  // re-injects even if the same template is picked twice.
  const handleSelectTemplate = (content) => {
    setTemplateInjection({ text: content, key: Date.now() });
    setShowQuickReplies(false);
  };

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <DashboardLayout
      userRole="guide"
      activeNavId="messages"
      onNavigate={handleNavigate}
      user={{
        name: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : 'Guide',
        email: user?.email || 'guide@outdoorguides.com',
        avatar: user?.profile_picture || null
      }}
    >
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
            <p className="mt-1 text-sm text-gray-500">
              Communicate with your customers about their bookings
            </p>
          </div>
          {unreadCount > 0 && (
            <span className="px-3 py-1 text-sm font-semibold text-white bg-rose-600 rounded-full">
              {unreadCount} unread
            </span>
          )}
        </div>

        {/* Messages Interface */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-3 divide-x divide-gray-200" style={{ height: 'calc(100vh - 300px)' }}>
            {/* Left Panel - Conversation List */}
            <div className="lg:col-span-1 flex flex-col overflow-y-auto">
              <ConversationList
                conversations={conversations}
                selectedConversationId={selectedConversationId}
                onSelectConversation={selectConversation}
                loading={loadingConversations}
                error={conversationsError}
              />
            </div>

            {/* Right Panel - Message Thread & Composer */}
            <div className="lg:col-span-2 flex flex-col bg-gray-50">
          {selectedConversationId ? (
            <>
              {/* Conversation Header */}
              <div className="bg-white border-b border-gray-200 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {selectedConversation?.participant_name || 'Unknown'}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {selectedConversation?.participant_user_type === 'guide' ? 'Guide' : 'Customer'}
                    </p>
                  </div>

                  <button
                    className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    title="More options"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-hidden">
                <MessageThread
                  messages={messages}
                  currentUserId={user?.id}
                  loading={loadingMessages}
                  error={messagesError}
                />
              </div>

              {/* Composer */}
              <MessageComposer
                onSendMessage={handleSendMessage}
                sending={sending}
                onOpenTemplates={() => setShowQuickReplies(true)}
                injectedText={templateInjection.text}
                injectionKey={templateInjection.key}
              />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <svg className="w-20 h-20 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <p className="text-lg font-medium">Select a conversation</p>
              <p className="text-sm text-gray-400 mt-1">Choose a conversation from the list to start messaging</p>
            </div>
          )}
            </div>
          </div>
        </div>

        {/* Quick Reply Selector Modal */}
        <QuickReplySelector
          isOpen={showQuickReplies}
          onClose={() => setShowQuickReplies(false)}
          onSelectTemplate={handleSelectTemplate}
          variables={{
            customer_name: selectedConversation?.participant_user_type === 'customer'
              ? selectedConversation?.participant_name?.split(' ')[0]
              : user?.first_name,
            guide_name: selectedConversation?.participant_user_type === 'guide'
              ? selectedConversation?.participant_name?.split(' ')[0]
              : user?.first_name,
          }}
        />
      </div>
    </DashboardLayout>
  );
};

export default Messages;
